import React, { useEffect, useState } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';

/**
 * Shows a fixed banner at the top of every tenant page when the logged-in
 * session is actually a super-admin impersonation. Reads `impersonation_active`
 * from localStorage which is set by SuperCompaniesPage.impersonate().
 *
 * "End session" clears the tenant token + marker and navigates back to
 * /superadmin (where the preserved `super_admin_token` still works).
 */
const ImpersonationBanner = () => {
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem('impersonation_active');
        if (!raw) { setInfo(null); return; }
        const parsed = JSON.parse(raw);
        // auto-clear if expired
        if (parsed.until && new Date(parsed.until).getTime() < Date.now()) {
          localStorage.removeItem('impersonation_active');
          localStorage.removeItem('token');
          setInfo(null);
          return;
        }
        setInfo(parsed);
      } catch {
        setInfo(null);
      }
    };
    load();
    const t = setInterval(() => { load(); setNow(Date.now()); }, 10000);
    return () => clearInterval(t);
  }, []);

  if (!info) return null;

  const endSession = () => {
    localStorage.removeItem('impersonation_active');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/superadmin';
  };

  const remaining = info.until ? Math.max(0, new Date(info.until).getTime() - now) : 0;
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldAlert className="w-4 h-4" />
        <span>
          Super-Admin is impersonating <b>{info.company || 'tenant admin'}</b>
          {info.until && (
            <span className="ml-2 text-amber-900">· {mins}m {secs.toString().padStart(2, '0')}s left</span>
          )}
        </span>
      </div>
      <button
        onClick={endSession}
        className="inline-flex items-center gap-1 text-xs font-semibold bg-black text-amber-400 px-3 py-1 rounded hover:bg-slate-900"
        data-testid="impersonation-end-btn"
      >
        <LogOut className="w-3 h-3" /> End session
      </button>
    </div>
  );
};

export default ImpersonationBanner;
